import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { openLink } from '@tma.js/sdk-react';
import { Loader2, Swords, Trophy, UserPlus, X } from 'lucide-react';

import { useHaptics } from '@/hooks/useHaptics';
import {
  useQueueEnterMutation,
  useQueueLeaveMutation,
  useQueueStatusQuery,
} from '@/services/qduelsApi';
import { classifyQduelsQueueError, type QduelsQueueErrorKind } from '@/services/qduelsErrors';
import { cn } from '@/lib/cn';

/** Auto-stop the search after this long with no opponent (spec default 3 min). */
const QUEUE_TIMEOUT_MS = 180_000;
/** Poll `GET /v1/queue/status` cadence while the sheet is searching. */
const QUEUE_POLL_MS = 3_000;
/** Canonical Clash-Royale open handoff (same target as MatchDetail). */
const CLASH_ROYALE_LINK = 'https://link.clashroyale.com';

interface QueueSheetProps {
  /** Render + drive the queue only while true. Parent mounts it on open so the
   *  internal state machine resets cleanly on every search. */
  open: boolean;
  /** Close handler — the sheet has already called queueLeave by the time it
   *  invokes this, so the parent only has to drop `open`. */
  onClose: () => void;
}

/**
 * The sheet's local lifecycle:
 *  - `entering`   — queueEnter in flight (initial + Riprova).
 *  - `searching`  — in queue, polling status, waiting for a pairing.
 *  - `found`      — status.currentMatch != null → "Avversario trovato!".
 *  - `timeout`    — elapsed >= QUEUE_TIMEOUT_MS with no match (queueLeave done).
 *  - `precheck`   — queueEnter rejected (not-linked / not-eligible / in-match).
 */
type Phase = 'entering' | 'searching' | 'found' | 'timeout' | 'precheck';

/** mm:ss for the count-up timer. */
function formatElapsed(ms: number): string {
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/**
 * Open-matchmaking bottom sheet. Mirrors ChallengeOptionsSheet's shell. On open
 * it enters the queue (fixed pool: REGULAR / bo1), polls status every ~3s, and
 * shows searching / found / not-found. ALWAYS calls queueLeave on cancel /
 * timeout / close to avoid ghost queue entries (backend has no TTL yet).
 */
export function QueueSheet({ open, onClose }: QueueSheetProps) {
  const { t } = useTranslation('duels');
  const haptics = useHaptics();
  const navigate = useNavigate();

  const [queueEnter] = useQueueEnterMutation();
  const [queueLeave] = useQueueLeaveMutation();

  const [phase, setPhase] = useState<Phase>('entering');
  const [precheck, setPrecheck] = useState<QduelsQueueErrorKind | null>(null);
  const [startedAt, setStartedAt] = useState<number>(() => Date.now());
  const [elapsed, setElapsed] = useState(0);

  // Poll status while searching AND after a match is found, so we can detect the
  // moment the paired match reaches `complete` and surface the result CTA. RTK
  // Query keeps polling as long as the hook stays mounted with a pollingInterval
  // AND skip=false.
  const searching = phase === 'searching';
  const found = phase === 'found';
  const statusQuery = useQueueStatusQuery(undefined, {
    skip: !open || (!searching && !found),
    pollingInterval: QUEUE_POLL_MS,
  });
  const status = statusQuery.data;
  const matchComplete = status?.currentMatch?.state === 'complete';
  const resultGameTag = status?.currentMatch?.gameTag;

  // Guard against double-leave (cancel then unmount, timeout then close, …) so
  // we never fire a redundant queueLeave. Reset whenever the sheet (re)opens.
  const leftRef = useRef(false);

  // Guard so `enter` fires EXACTLY ONCE per sheet-open. The sheet is freshly
  // mounted by the parent on every open (`{queueOpen && <QueueSheet .../>}`),
  // so this ref starts false on each open and flips true on the first enter.
  // Without it the open-effect could re-fire (e.g. if `enter`'s identity ever
  // changed) and spam POST /v1/queue/enter — the original PROD bug.
  const enteredRef = useRef(false);

  const leaveQueue = useCallback(() => {
    if (leftRef.current) return;
    leftRef.current = true;
    // Fire-and-forget — closing the UI must not wait on the network, and a
    // failed leave is non-fatal (worst case a stale entry the backend TTL
    // follow-up will sweep). The catch keeps it from becoming an unhandled
    // rejection.
    queueLeave()
      .unwrap()
      .catch(() => {
        /* best-effort cleanup */
      });
  }, [queueLeave]);

  // Enter (or re-enter) the queue. Used on open (via the guarded effect below)
  // and on Riprova. On failure it lands in a TERMINAL `precheck` phase — it does
  // NOT retry, so a 403 not_eligible produces exactly one call, not a loop.
  const enter = useCallback(async () => {
    leftRef.current = false;
    setPrecheck(null);
    setPhase('entering');
    setStartedAt(Date.now());
    setElapsed(0);
    try {
      await queueEnter({ gameMode: 'REGULAR', format: 'bo1' }).unwrap();
      setPhase('searching');
    } catch (err) {
      const kind = classifyQduelsQueueError(err);
      haptics.notification('error');
      setPrecheck(kind);
      setPhase('precheck');
    }
  }, [queueEnter, haptics]);

  // Keep a ref to the latest `enter` so the open-effect can call it WITHOUT
  // listing `enter` (or its transitive deps like `haptics`) as a dependency.
  // This is what makes the open-effect immune to identity churn: it can only
  // fire on an actual open transition, never on a re-render / state / error /
  // status-poll change.
  const enterRef = useRef(enter);
  enterRef.current = enter;

  // Enter the queue EXACTLY ONCE per open. Deps are `[open]` only; the
  // `enteredRef` guard makes it idempotent even if React were to re-run the
  // effect, and it resets on close so the next open enters cleanly.
  useEffect(() => {
    if (!open) {
      enteredRef.current = false;
      return;
    }
    if (enteredRef.current) return;
    enteredRef.current = true;
    void enterRef.current();
  }, [open]);

  // Count-up timer + timeout watchdog, only while searching.
  useEffect(() => {
    if (!open || !searching) return;
    const id = window.setInterval(() => {
      const next = Date.now() - startedAt;
      setElapsed(next);
      if (next >= QUEUE_TIMEOUT_MS) {
        leaveQueue();
        haptics.notification('warning');
        setPhase('timeout');
      }
    }, 1_000);
    return () => window.clearInterval(id);
  }, [open, searching, startedAt, leaveQueue, haptics]);

  // Transition to "found" the moment the poll reports an assigned match.
  useEffect(() => {
    if (searching && status?.currentMatch) {
      haptics.notification('success');
      setPhase('found');
    }
  }, [searching, status?.currentMatch, haptics]);

  // Safety net: if the sheet unmounts while still in the queue (app close,
  // route change), leave the queue.
  useEffect(() => {
    return () => {
      leaveQueue();
    };
  }, [leaveQueue]);

  if (!open) return null;

  const close = () => {
    leaveQueue();
    onClose();
  };

  const openClashRoyale = () => {
    haptics.impact('medium');
    openLink.ifAvailable(CLASH_ROYALE_LINK);
    onClose();
  };

  const viewResult = () => {
    if (!resultGameTag) return;
    haptics.impact('medium');
    leaveQueue();
    onClose();
    navigate(`/duels/result/${encodeURIComponent(resultGameTag)}`);
  };

  const goLink = () => {
    haptics.impact('light');
    onClose();
    navigate('/duels/link');
  };

  const goChallengeFriend = () => {
    haptics.impact('light');
    leaveQueue();
    onClose();
    navigate('/friends?tab=duels');
  };

  const retry = () => {
    haptics.impact('medium');
    void enter();
  };

  // Surface the "Sfida un amico" CTA early when the caller is alone in the pool.
  const aloneInQueue = (status?.searching ?? 0) <= 1;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center"
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        aria-label={t('queue.cancel', { defaultValue: 'Cancel' })}
        onClick={close}
        className="absolute inset-0 bg-black/60"
      />
      <div className="relative w-full max-w-md rounded-t-2xl bg-bg-secondary p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-base font-bold text-fg">
            <Swords className="h-4 w-4 text-brand" />
            {t('queue.find', { defaultValue: 'Find match' })}
          </h2>
          <button
            type="button"
            onClick={close}
            aria-label={t('queue.cancel', { defaultValue: 'Cancel' })}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-bg-tertiary text-fg-muted active:opacity-80"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* ── searching / entering ─────────────────────────────────────── */}
        {(phase === 'entering' || phase === 'searching') && (
          <div className="flex flex-col items-center gap-4 py-4">
            <Loader2 className="h-10 w-10 animate-spin text-brand" />
            <div className="text-center">
              <p className="text-sm font-semibold text-fg">
                {t('queue.searching', {
                  defaultValue: 'Searching for an opponent…',
                })}
              </p>
              <p className="mt-1 text-xs text-fg-muted">
                {t('queue.inQueue', {
                  n: status?.searching ?? 1,
                  defaultValue: '{{n}} in queue',
                })}
                {' · '}
                {formatElapsed(elapsed)}
              </p>
            </div>

            <button
              type="button"
              onClick={close}
              className="w-full rounded-xl bg-bg-tertiary py-3 text-sm font-bold text-fg-muted transition-opacity active:opacity-80"
            >
              {t('queue.cancel', { defaultValue: 'Cancel' })}
            </button>

            {phase === 'searching' && aloneInQueue && (
              <button
                type="button"
                onClick={goChallengeFriend}
                className="flex w-full items-center justify-center gap-2 text-xs font-semibold text-brand active:opacity-80"
              >
                <UserPlus className="h-4 w-4" />
                {t('queue.challengeFriend', {
                  defaultValue: 'Challenge a friend',
                })}
              </button>
            )}
          </div>
        )}

        {/* ── found ────────────────────────────────────────────────────── */}
        {phase === 'found' && (
          <div className="flex flex-col items-center gap-4 py-4">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-brand/15 text-brand">
              <Swords className="h-6 w-6" />
            </span>
            <p className="text-base font-bold text-fg">
              {matchComplete
                ? t('queue.matchComplete', { defaultValue: 'Match complete!' })
                : t('queue.found', { defaultValue: 'Opponent found!' })}
            </p>
            {matchComplete && resultGameTag ? (
              <button
                type="button"
                onClick={viewResult}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand py-3 text-sm font-bold text-brand-fg transition-opacity active:opacity-80"
              >
                <Trophy className="h-4 w-4" />
                {t('queue.viewResult', { defaultValue: 'View result' })}
              </button>
            ) : (
              <button
                type="button"
                onClick={openClashRoyale}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand py-3 text-sm font-bold text-brand-fg transition-opacity active:opacity-80"
              >
                <Swords className="h-4 w-4" />
                {t('queue.openCR', { defaultValue: 'Open Clash Royale' })}
              </button>
            )}
          </div>
        )}

        {/* ── timeout / not-found ──────────────────────────────────────── */}
        {phase === 'timeout' && (
          <div className="flex flex-col items-center gap-3 py-4">
            <p className="text-sm font-semibold text-fg">
              {t('queue.notFound', { defaultValue: 'No opponent found' })}
            </p>
            <button
              type="button"
              onClick={retry}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand py-3 text-sm font-bold text-brand-fg transition-opacity active:opacity-80"
            >
              {t('queue.retry', { defaultValue: 'Try again' })}
            </button>
            <button
              type="button"
              onClick={goChallengeFriend}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-bg-tertiary py-3 text-sm font-bold text-fg-muted transition-opacity active:opacity-80"
            >
              <UserPlus className="h-4 w-4" />
              {t('queue.challengeFriend', { defaultValue: 'Challenge a friend' })}
            </button>
          </div>
        )}

        {/* ── pre-check error ──────────────────────────────────────────── */}
        {phase === 'precheck' && (
          <QueuePrecheck
            kind={precheck}
            onLink={goLink}
            onOpenCR={openClashRoyale}
            onClose={close}
          />
        )}
      </div>
    </div>
  );
}

/** The pre-check / enter-error body, split out to keep the state machine tidy. */
function QueuePrecheck({
  kind,
  onLink,
  onOpenCR,
  onClose,
}: {
  kind: QduelsQueueErrorKind | null;
  onLink: () => void;
  onOpenCR: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation('duels');

  if (kind === 'player_not_linked') {
    return (
      <div className="flex flex-col items-center gap-3 py-4">
        <p className="text-center text-sm font-semibold text-fg">
          {t('subtitle', {
            defaultValue:
              'Link your Clash Royale account to join tournaments, challenge friends to duels, and see your stats.',
          })}
        </p>
        <button type="button" onClick={onLink} className={primaryBtn}>
          {t('queue.linkAccount', { defaultValue: 'Link account' })}
        </button>
      </div>
    );
  }

  if (kind === 'not_eligible') {
    return (
      <div className="flex flex-col items-center gap-3 py-4">
        <p className="text-center text-sm font-semibold text-fg">
          {t('queue.notEligible', {
            defaultValue: 'You need ≥32 cards and level ≥10',
          })}
        </p>
        <button type="button" onClick={onClose} className={secondaryBtn}>
          {t('queue.cancel', { defaultValue: 'Cancel' })}
        </button>
      </div>
    );
  }

  if (kind === 'already_in_match') {
    return (
      <div className="flex flex-col items-center gap-3 py-4">
        <p className="text-center text-sm font-semibold text-fg">
          {t('queue.alreadyInMatch', {
            defaultValue: 'You already have a match in progress',
          })}
        </p>
        <button type="button" onClick={onOpenCR} className={primaryBtn}>
          {t('queue.openCR', { defaultValue: 'Open Clash Royale' })}
        </button>
      </div>
    );
  }

  // auth / network / server / unknown — generic copy.
  return (
    <div className="flex flex-col items-center gap-3 py-4">
      <p className="text-center text-sm font-semibold text-fg">
        {t('queue.error', { defaultValue: 'Could not join the queue. Try again.' })}
      </p>
      <button type="button" onClick={onClose} className={secondaryBtn}>
        {t('queue.cancel', { defaultValue: 'Cancel' })}
      </button>
    </div>
  );
}

const primaryBtn = cn(
  'flex w-full items-center justify-center gap-2 rounded-xl bg-brand py-3',
  'text-sm font-bold text-brand-fg transition-opacity active:opacity-80',
);
const secondaryBtn = cn(
  'flex w-full items-center justify-center gap-2 rounded-xl bg-bg-tertiary py-3',
  'text-sm font-bold text-fg-muted transition-opacity active:opacity-80',
);
